import { getManhattanDistance } from "utils/math/geometry/getManhattanDistance";
import {
  FindClosestOrthogonalProjection,
  FindClosestOrthogonalProjectionResult,
} from "./types";

export const findClosestOrthogonalProjection: FindClosestOrthogonalProjection =
  ({ point, topology }) => {
    let minDist = Infinity;
    let bestResult: FindClosestOrthogonalProjectionResult = null;

    const { x: px, y: py } = point;
    const nodeCoords = topology.nodes;

    for (const edge of topology.edges ?? []) {
      const n1 = nodeCoords[edge.from];
      const n2 = nodeCoords[edge.to];

      // Skip if nodes are missing or segment has zero length
      if (!n1 || !n2) continue;
      if (n1.x === n2.x && n1.y === n2.y) continue;

      const x1 = n1.x;
      const y1 = n1.y;
      const x2 = n2.x;
      const y2 = n2.y;

      const dx = x2 - x1;
      const dy = y2 - y1;

      // Vector projection math
      const denom = dx * dx + dy * dy;
      const rawT = ((px - x1) * dx + (py - y1) * dy) / denom;
      const t = Math.max(0, Math.min(1, rawT));

      const projX = x1 + t * dx;
      const projY = y1 + t * dy;

      // We compare the input 'point' with the calculated 'proj' point
      const dist = getManhattanDistance({
        pointA: point,
        pointB: { x: projX, y: projY },
      });

      if (dist < minDist) {
        minDist = dist;

        bestResult = {
          segment: {
            fromNodeId: edge.from,
            toNodeId: edge.to,
          },
          projection: {
            projectedPoint: { x: projX, y: projY },
            routeCorner: { x: px, y: projY }, // vertical-first
            distance: dist,
          },
        };
      }
    }

    return bestResult;
  };
